import { resolvePrimitiveOperationInput } from '@/adapters/primitive/_pure/interaction/operationInput';
import { derivePrimitiveStepStatesFromOperationInput } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { buildBooleanAssignedWheels, buildBooleanDraftRod } from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import type { Config } from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInputDraft } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

/** A value-independent envelope includes incomplete assignments and every Boolean execution. */
export function deriveBooleanCameraSteps(
    config: Config, operation: PrimitiveOperationInputDraft['operation'],
): readonly PrimitiveStepState[] | undefined {
    const values = ['', 'true', 'false'] as const;
    let drafts: PrimitiveOperationInputDraft[];
    if (operation === 'logical-not') {
        drafts = values.map((inputText) => ({ operation, inputText }));
    } else if (operation === 'logical-and' || operation === 'logical-nand' || operation === 'logical-or'
        || operation === 'logical-nor' || operation === 'logical-xor' || operation === 'logical-xnor') {
        drafts = values.flatMap((operandOneText) => values.map((operandTwoText) =>
            ({ operation, operandOneText, operandTwoText })));
    } else return undefined;
    return drafts.flatMap((draft): PrimitiveStepState[] => {
        const booleanRod = buildBooleanDraftRod(config, draft);
        if (!booleanRod) throw new Error(`Boolean camera requires draft geometry for ${operation}.`);
        const input = resolvePrimitiveOperationInput(draft);
        return [{
            stepKey: `camera-draft:${JSON.stringify(draft)}`, label: 'Draft', description: 'Boolean assignment envelope',
            keyframes: [{ wheels: buildBooleanAssignedWheels(config, draft), booleanRod, transition: 'instant', events: [] }],
        }, ...(input ? derivePrimitiveStepStatesFromOperationInput({ config, input }) : [])];
    });
}
